import { ReactNode, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { isManager } from "../utils/helpers";

interface OnboardingGuardProps {
  children: ReactNode;
}

/**
 * OnboardingGuard - Componente que verifica se o usuário precisa completar o onboarding
 * e redireciona para a página correta se necessário.
 *
 * Deve ser usado para proteger rotas que requerem onboarding completo.
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { user, loadingUserData } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Não fazer nada enquanto está carregando os dados do usuário
    if (loadingUserData || !user) {
      return;
    }

    // Se já estiver na página de onboarding, não redirecionar
    if (location.pathname.includes("/onboarding")) {
      return;
    }

    // Verificar se o usuário precisa fazer onboarding
    const needsOnboarding =
      user.extra?.onboarding === undefined || user.extra?.onboarding === true;

    if (needsOnboarding) {
      const isGestor = isManager(user);

      // Redirecionar para o onboarding apropriado
      if (isGestor) {
        navigate("/onboarding/gestor", { replace: true });
      } else {
        navigate("/onboarding", { replace: true });
      }
    }
  }, [user, loadingUserData, navigate, location.pathname]);

  // Mostrar loading enquanto verifica
  if (loadingUserData) {
    return null; // ou um componente de loading
  }

  return <>{children}</>;
}
